import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LoginComponent } from "./views/auth/login/login.component";
import { ForgotPasswordComponent } from "./views/auth/forgot-password/forgot-password.component";
import { NotFoundComponent } from "./views/errors/not-found/not-found.component";
import { ResetPasswordComponent } from "./views/auth/reset-password/reset-password.component";
import { RedirectAuthenticatedGuard } from "./core/guards/redirect-authenticated.guard";
import { DashboardComponent } from './layouts/dashboard/dashboard.component';
import { AdminDashboardComponent } from './standalone/admin-dashboard/admin-dashboard.component';
import { requireBatteriesPageAccess } from './features/batteries/batteries-route-access.guard';
import {
  requireAdminOrgEventsAccess,
  requireAdminProjectsAccess,
  requireAuth,
  requireBatteryCatalogAdminAccess,
  requireCanAdvanceOrderStatus,
  requireCustomerProfilesAdminAccess,
  requireFullAdminOrgAccess,
  requireResellerEventsAccess,
  requireResellerWorkspaceAccess,
} from './core/auth/route-access.guard';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [RedirectAuthenticatedGuard],
    children: []
  },
  {
    path: 'login',
    component: LoginComponent
  },
  {
    path: 'reset-password',
    component: ForgotPasswordComponent
  },
  {
    path: 'reset-password/:token',
    component: ResetPasswordComponent
  },
  {
    path: 'projects',
    canActivate: [requireAuth],
    loadChildren: () => import('./features/projects/projects.module').then(m => m.ProjectsModule)
  },
  {
    path: 'work-orders',
    canActivate: [requireAuth],
    loadChildren: () => import('./features/work-orders/work-orders.module').then(m => m.WorkOrdersModule),
  },
  {
    path: 'users',
    canActivate: [requireAuth, requireResellerWorkspaceAccess],
    loadChildren: () => import('./features/users/users.module').then(m => m.UsersModule)
  },
  {
    path: 'profile',
    canActivate: [requireAuth],
    loadChildren: () => import('./features/profile/profile.module').then(m => m.ProfileModule)
  },
  {
    path: 'batteries/cabinet-models/create',
    pathMatch: 'full',
    redirectTo: 'cabinets/models/create',
  },
  {
    path: 'batteries/cabinet-models/:id',
    pathMatch: 'full',
    redirectTo: 'cabinets/models/:id',
  },
  {
    path: 'batteries/cabinet-models',
    pathMatch: 'full',
    redirectTo: 'cabinets/models',
  },
  {
    path: 'batteries',
    canActivate: [requireAuth, requireResellerWorkspaceAccess, requireBatteriesPageAccess],
    loadChildren: () => import('./features/batteries/battery-models.module').then(m => m.BatteryModelsModule)
  },
  {
    path: 'cabinets',
    canActivate: [requireAuth, requireResellerWorkspaceAccess, requireBatteriesPageAccess],
    loadChildren: () => import('./features/cabinets/cabinets.module').then(m => m.CabinetsModule)
  },
  {
    path: 'events',
    canActivate: [requireAuth, requireResellerEventsAccess],
    loadChildren: () => import('./features/events/events.module').then(m => m.EventsModule)
  },
  {
    path: 'customer-profiles',
    canActivate: [requireAuth, requireResellerWorkspaceAccess],
    loadChildren: () => import('./features/customer-profiles/customer-profiles.module').then(m => m.CustomerProfilesModule)
  },
  {
    path: 'notifications',
    canActivate: [requireAuth],
    component: DashboardComponent,
    children: [
      {
        path: '',
        loadChildren: () => import('./features/notifications/notifications.module').then(m => m.NotificationsModule)
      }
    ]
  },
  {
    path: 'admin',
    canActivate: [requireAuth, requireAdminProjectsAccess],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'projects',
      },
      {
        path: 'dashboard',
        canActivate: [requireFullAdminOrgAccess],
        component: DashboardComponent,
        title: 'Admin dashboard',
        children: [
          {
            path: '',
            component: AdminDashboardComponent,
          }
        ]
      },
      {
        path: 'resellers',
        canActivate: [requireFullAdminOrgAccess],
        loadChildren: () => import('./features/resellers/resellers.module').then(m => m.ResellersModule),
      },
      {
        path: 'users',
        canActivate: [requireFullAdminOrgAccess],
        loadChildren: () => import('./features/users/users.module').then(m => m.UsersModule),
      },
      {
        path: 'manufacturers',
        pathMatch: 'full',
        redirectTo: 'batteries/manufacturers',
      },
      {
        path: 'manufacturers/create',
        pathMatch: 'full',
        redirectTo: 'batteries/manufacturers/create',
      },
      {
        path: 'manufacturers/:id',
        pathMatch: 'full',
        redirectTo: 'batteries/manufacturers/:id',
      },
      {
        path: 'batteries/cabinet-models/create',
        pathMatch: 'full',
        redirectTo: 'cabinets/models/create',
      },
      {
        path: 'batteries/cabinet-models/:id',
        pathMatch: 'full',
        redirectTo: 'cabinets/models/:id',
      },
      {
        path: 'batteries/cabinet-models',
        pathMatch: 'full',
        redirectTo: 'cabinets/models',
      },
      {
        path: 'batteries',
        canActivate: [requireBatteryCatalogAdminAccess],
        loadChildren: () => import('./features/batteries/battery-models.module').then(m => m.BatteryModelsModule)
      },
      {
        path: 'cabinets',
        canActivate: [requireBatteryCatalogAdminAccess],
        loadChildren: () => import('./features/cabinets/cabinets.module').then(m => m.CabinetsModule)
      },
      {
        // /admin/cables
        path: 'cables',
        canActivate: [requireFullAdminOrgAccess],
        loadChildren: () => import('./features/cables/cables.module').then(m => m.CablesModule),
      },
      {
        path: 'projects',
        loadChildren: () => import('./features/projects/projects.module').then(m => m.ProjectsModule),
      },
      {
        path: 'work-orders',
        loadChildren: () => import('./features/work-orders/work-orders.module').then(m => m.WorkOrdersModule),
      },
      {
        // /admin/production
        path: 'production',
        canActivate: [requireCanAdvanceOrderStatus],
        loadChildren: () => import('./features/production/production.module').then(m => m.ProductionModule),
      },
      {
        path: 'customer-profiles',
        canActivate: [requireCustomerProfilesAdminAccess],
        loadChildren: () => import('./features/customer-profiles/customer-profiles.module').then(m => m.CustomerProfilesModule),
      },
      {
        path: 'events',
        canActivate: [requireAdminOrgEventsAccess],
        loadChildren: () => import('./features/events/events.module').then(m => m.EventsModule),
      },
      {
        path: 'notifications',
        component: DashboardComponent,
        children: [
          {
            path: '',
            loadChildren: () => import('./features/notifications/notifications.module').then(m => m.NotificationsModule)
          }
        ]
      },
      {
        path: 'part-numbers',
        canActivate: [requireFullAdminOrgAccess],
        loadChildren: () => import('./features/parts-admin/parts-admin.module').then(m => m.PartsAdminModule)
      },
      {
        path: 'installation-pricing',
        canActivate: [requireFullAdminOrgAccess],
        loadChildren: () => import('./features/installation-pricing/installation-pricing.module').then(
          m => m.InstallationPricingModule
        )
      },
    ]
  },
  {
    path: '**',
    component: NotFoundComponent
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
